import type { VoucherProviderResponse } from '@/api/model/voucherProviderResponse';
import type { VoucherProviderUpdate } from '@/api/model/voucherProviderUpdate';
import { useUpdateProvider } from '@/api/voucher-providers/voucher-providers';
import React, { useEffect } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api-error';
import { VoucherProviderForm, type VoucherProviderFormData } from './VoucherProviderForm';

interface VoucherProviderEditFormProps {
  provider: VoucherProviderResponse;
  onSuccess?: () => void;
  onCancel?: () => void;
  onFormDataChange?: (data: VoucherProviderFormData) => void;
  hideLogoField?: boolean;
  onFormInstance?: (form: UseFormReturn<VoucherProviderFormData>) => void;
}

export const VoucherProviderEditForm: React.FC<VoucherProviderEditFormProps> = ({
  provider,
  onSuccess,
  onCancel,
  onFormDataChange,
  hideLogoField = false,
  onFormInstance,
}) => {
  const navigate = useNavigate();

  const form = useForm<VoucherProviderFormData>({
    defaultValues: {
      name: provider.name,
      email: provider.email,
      address: provider.address,
      phone: provider.phone || '',
      short_description: provider.short_description || '',
      logo_urls: provider.logo_url ? [provider.logo_url] : [],
      category: provider.category,
    },
  });

  const updateProviderMutation = useUpdateProvider();

  const watchedData = useWatch({ control: form.control });

  // Watch for form changes and update preview
  useEffect(() => {
    if (!onFormDataChange || !watchedData) return;
    onFormDataChange(watchedData as VoucherProviderFormData);
  }, [onFormDataChange, watchedData]);

  // Pass form instance to parent
  useEffect(() => {
    if (onFormInstance) {
      onFormInstance(form);
    }
  }, [form, onFormInstance]);

  const onSubmit = (data: VoucherProviderFormData) => {
    // Extract logo URL from uploaded images array
    const logoUrl = data.logo_urls && data.logo_urls.length > 0 ? data.logo_urls[0] : undefined;

    const providerData: VoucherProviderUpdate = {
      name: data.name,
      email: data.email,
      address: data.address,
      phone: data.phone || undefined,
      short_description: data.short_description || undefined,
      logo_url: logoUrl,
      category: data.category,
    };

    updateProviderMutation
      .mutateAsync({
        providerId: provider.id,
        data: providerData,
      })
      .then(() => {
        toast.success('Gutscheinanbieter erfolgreich aktualisiert');
        onSuccess?.();
      })
      .catch(error => {
        toast.error(getApiErrorMessage(error, 'Aktualisieren des Gutscheinanbieters fehlgeschlagen'));
      });
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/admin/providers');
    }
  };

  return (
    <VoucherProviderForm
      form={form}
      onSubmit={onSubmit}
      isSubmitting={updateProviderMutation.isPending}
      submitText='Anbieter aktualisieren'
      onCancel={handleCancel}
      cancelText='Abbrechen'
      hideLogoField={hideLogoField}
    />
  );
};
